import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Image, SearchBar, Typography, LoadingIndicator, Pagination } from 'lib/components';
import { HospitalsDataTable } from 'lib/components/DataTables';
import {
	useAppDispatch,
	actions,
	useSelectHospitals,
	useSelectAuthorization,
	useSelectRequest,
	RequestManager,
} from 'store';
import { useComprehensiveTopNav, useWindowResize, useSearch, usePagination } from 'hooks';
import classes from './index.module.scss';

/**
 * Hospitals management screen component
 * Displays a list of hospitals with search, pagination, and management capabilities
 * Allows creating and deleting hospitals based on user permissions
 */
const HospitalsScreen = () => {
	// Responsive design helpers
	const { isSmallDevice, isSmallerDevice } = useWindowResize();

	// Redux state and actions
	const dispatch = useAppDispatch();
	// Hospital data from Redux store
	const hospitals = useSelectHospitals();
	// Active API request tracking
	const { active } = useSelectRequest();
	// Permission checks for hospital management
	const { canCreateHospital, canDeleteHospital } = useSelectAuthorization();

	// Search query for filtering hospitals
	const [query, setQuery] = useState('');
	// ID of the currently selected hospital (for actions like delete)
	const [checked, setChecked] = useState<number[]>([]);

	/**
	 * Fetch hospitals data and BuddyPunch locations on component mount
	 */
	useEffect(() => {
		dispatch(actions.fetchHospitals());
		dispatch(actions.getBuddyPunchLocations());
	}, []);

	/**
	 * Monitor API requests
	 * Currently just checks if the request manager is idle
	 */
	useEffect(() => {
		const RM = new RequestManager(active);
		if (RM.idle()) return;
	}, [active]);

	/**
	 * Set up pagination for hospitals list
	 * Returns data and handlers for paginated display
	 */
	const { isDataFetched, listing, setData, paginatedData, paginationProps } = usePagination(hospitals, {});

	/**
	 * Handle search functionality using custom hook
	 * Searches across hospital name, email, and phone number
	 */
	useSearch(
		query,
		// Reset function - called when query is empty
		() => {
			setData(listing);
		},
		// Search function - called when query has content
		() => {
			const result = listing.filter(
				(hospital) =>
					hospital.name.toLowerCase().search(query.toLowerCase()) !== -1 ||
					(hospital.email && hospital.email.toLowerCase().search(query.toLowerCase()) !== -1) ||
					(hospital.phoneNumber && hospital.phoneNumber.toLowerCase().search(query.toLowerCase()) !== -1)
			);
			setData(result);
		}
	);

	/**
	 * Handle checkbox selection for hospital rows
	 * Only one hospital can be selected at a time for actions
	 *
	 * @param id - ID of the hospital being checked/unchecked
	 * @param isChecked - Whether the checkbox is being checked or unchecked
	 */
	const handleCheckAction = (id: number, isChecked: boolean) => {
		if (isChecked && !checked.includes(id)) {
			setChecked([id]);
			return;
		}

		if (!isChecked || checked.includes(id)) {
			setChecked([]);
		}
	};

	/**
	 * Handle hospital deletion action
	 * Dispatches delete action for the selected hospital
	 */
	const handleDelete = () => {
		dispatch(actions.deleteHospital(checked[0]));
	};

	// Set up the top navigation bar with title
	useComprehensiveTopNav('Hospitals');

	// Show loading indicator while data is being fetched
	if (!isDataFetched) return <LoadingIndicator />;

	return (
		<div className={classes['hospitals']}>
			<div className={classes['hospitals-top']}>
				<SearchBar
					query={query}
					setQuery={setQuery}
					onSearch={() => console.log({ query })}
					className={classes['search-input-container']}
					placeholder="Search..."
				/>
				{canCreateHospital && (
					<Link to="/hospitals/new" className={classes['hospitals-top-addNewHospital']}>
						<span className={classes['hospitals-top-addNewHospital-title']}>
							<Typography variant={isSmallDevice ? 'h5' : 'h6'} weight="medium">
								Add New Hospital
							</Typography>
						</span>
						<div className={classes['hospitals-top-addNewHospital-icon']}>+</div>
					</Link>
				)}
			</div>
			<div className={classes['row']} style={{ justifyContent: 'space-between' }}>
				<div className={classes['row']} style={{ marginTop: isSmallerDevice ? 0 : '0.5rem' }}>
					{checked.length !== 0 && (
						<>
							{canDeleteHospital && (
								<button
									className={classes['action-button']}
									style={{
										backgroundColor: '#FCD3D1',
										borderColor: '#FF3B30',
									}}
									onClick={handleDelete}
								>
									<Image
										src="icTrashDanger"
										style={{
											marginRight: '0.5rem',
										}}
									/>
									<Typography variant={isSmallerDevice ? 'h5' : 'h6'} color="danger">
										Delete
									</Typography>
								</button>
							)}
						</>
					)}
				</div>
			</div>
			<HospitalsDataTable
				hospitals={paginatedData}
				checkedId={checked[0] ?? -1}
				checkAction={handleCheckAction}
			/>
			<Pagination {...paginationProps} />
		</div>
	);
};

export default HospitalsScreen;
